import { useState, useEffect } from "react";
import { QrReader } from "react-qr-reader";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { detectAndValidateQR, getQRTypeName, QRCodeType } from "@/utils/qrCodeDetector";
import { Loader2, QrCode, CheckCircle2, XCircle, ImageIcon, Scan, Flashlight, FlashlightOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGalleryQRScanner } from "@/hooks/useGalleryQRScanner";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";

interface UniversalQRScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (type: QRCodeType, data: any) => void;
  allowedTypes?: QRCodeType[];
  currentContext?: 'p2p' | 'payment' | 'receipt' | 'deposit' | 'any';
}

export function UniversalQRScanner({ 
  isOpen, 
  onClose, 
  onResult,
  allowedTypes,
  currentContext = 'any'
}: UniversalQRScannerProps) {
  const [scanning, setScanning] = useState(false);
  const [detectedType, setDetectedType] = useState<QRCodeType | null>(null);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [scanError, setScanError] = useState(false);
  const [flashlightOn, setFlashlightOn] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Gallery QR Scanner hook
  const { pickAndDecodeQR, isLoading: galleryLoading, cleanup } = useGalleryQRScanner();

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const resetState = () => {
    setScanning(false);
    setDetectedType(null);
    setScanSuccess(false);
    setScanError(false);
  };

  // Haptic feedback helper
  const triggerHaptic = async (style: ImpactStyle = ImpactStyle.Medium) => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style });
      } catch (err) {
        console.log('Haptic not available');
      }
    }
  };

  const processQRData = async (rawData: string) => {
    setScanning(true);
    
    // Detect and validate QR Code
    const result = detectAndValidateQR(rawData);
    setDetectedType(result.type);
    
    // Check if QR type is allowed
    if (allowedTypes && !allowedTypes.includes(result.type)) {
      setScanError(true);
      await triggerHaptic(ImpactStyle.Heavy);
      toast({
        title: `⚠️ ${t('qrScanner.errors.invalidQR')}`,
        description: `${getQRTypeName(result.type)} ${t('qrScanner.errors.wrongType')}`,
        variant: "destructive",
        action: result.suggestedRoute ? (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              navigate(result.suggestedRoute!);
              onClose();
            }}
          >
            {t('qrScanner.goToCorrectPage')}
          </Button>
        ) : undefined
      });
      
      setTimeout(() => {
        setScanError(false);
        setScanning(false);
      }, 2000);
      return;
    }
    
    if (!result.isValid) {
      setScanError(true);
      await triggerHaptic(ImpactStyle.Heavy);
      toast({
        title: `❌ ${t('qrScanner.errors.invalidQR')}`,
        description: result.errorMessage || t('qrScanner.errors.unknown'),
        variant: "destructive"
      });
      
      setTimeout(() => {
        setScanError(false);
        setScanning(false);
      }, 2000);
      return;
    }
    
    // Show success with haptic
    setScanSuccess(true);
    await triggerHaptic(ImpactStyle.Medium);
    toast({
      title: `✅ ${t('qrScanner.scanSuccess')}`,
      description: getQRTypeName(result.type),
      duration: 2000
    });
    
    // If custom handler provided, use it
    if (onResult) {
      setTimeout(() => {
        onResult(result.type, result.data);
        resetState();
        onClose();
      }, 1000);
      return;
    }
    
    // Otherwise, auto-navigate based on type
    setTimeout(() => {
      switch (result.type) {
        case 'p2p_transfer':
          navigate('/p2p-transfer', { state: { qrData: result.data } });
          break;
          
        case 'payment_gateway': {
          const pid = result.data?.payment_id;

          if (pid) {
            // มี payment_id → ไปหน้า /pay/:id ได้เลย
            navigate(`/pay/${pid}`);
          } else {
            // ไม่มี payment_id → แสดง error และ fallback กลับหน้า /qr
            triggerHaptic(ImpactStyle.Heavy);
            toast({
              title: "⚠️ QR Code ไม่สมบูรณ์",
              description: "ไม่พบ Payment ID ใน QR Code นี้",
              variant: "destructive",
              action: result.data?.url ? (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    window.open(result.data.url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  เปิดลิงก์ภายนอก
                </Button>
              ) : undefined
            });
            navigate('/qr');
          }
          break;
        }
          
        case 'receipt':
          if (result.data.url) {
            navigate(result.data.url);
          }
          break;
          
        case 'deposit_qr':
          navigate('/deposit', { state: { bankQR: result.data } });
          break;
      }
      
      resetState();
      onClose();
    }, 1000);
  };

  const handleScan = async (rawData: string | null) => {
    if (!rawData || scanning) return;
    await processQRData(rawData);
  };

  const handleGalleryPick = async () => {
    if (scanning || galleryLoading) return;
    
    try {
      const result = await pickAndDecodeQR();
      
      if (result.success && result.data) {
        await processQRData(result.data);
      } else if (result.error && !result.error.includes('ยกเลิก')) {
        toast({
          title: `❌ ${t('qrScanner.noQRFound')}`,
          description: result.error,
          variant: "destructive"
        });
      }
    } catch (err: any) {
      console.error('Gallery pick error:', err);
      
      // Check if it's a Capacitor API error on web
      if (err.message?.includes('setPhotoOptions') || err.message?.includes('not implemented')) {
        toast({
          title: "ฟีเจอร์นี้ไม่รองรับบน Browser",
          description: "กรุณาใช้กล้องสแกนโดยตรง หรือดาวน์โหลดแอปเพื่อใช้งานเลือกรูปจากคลัง",
          variant: "destructive"
        });
      } else {
        toast({
          title: `❌ เกิดข้อผิดพลาด`,
          description: err.message || "ไม่สามารถเปิดคลังรูปภาพได้",
          variant: "destructive"
        });
      }
    }
  };

  const handleError = (err: any) => {
    // ถ้าเป็น setPhotoOptions error หรือ constraint error ให้ ignore (ไม่ crash)
    if (err?.message?.includes('setPhotoOptions') || 
        err?.message?.includes('constraint') ||
        err?.message?.includes('OverconstrainedError') ||
        err?.message?.includes('No MultiFormat')) {
      console.warn('Camera constraints not supported, continuing:', err.message);
      return; // ทำต่อได้โดยไม่ crash
    }
    
    console.error('QR Scanner Error:', err);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const toggleFlashlight = () => {
    setFlashlightOn(!flashlightOn);
    triggerHaptic(ImpactStyle.Light);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden bg-background/95 backdrop-blur-xl border-primary/20">
        <DialogHeader className="p-4 pb-3 border-b border-border/50">
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
              <QrCode className="h-5 w-5 text-primary" />
            </div>
            <div>
              <span className="text-lg font-semibold">{t('qrScanner.title')}</span>
              <p className="text-xs text-muted-foreground font-normal mt-0.5">
                {t('qrScanner.alignQRCode')}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {/* Scanner Area */}
        <div className="relative aspect-square bg-black overflow-hidden">
          <QrReader
            onResult={(result, error) => {
              if (result) handleScan(result.getText());
              if (error) handleError(error);
            }}
            constraints={{ facingMode: 'environment' }}
            scanDelay={300}
            containerStyle={{ width: "100%", height: "100%" }}
          />
          
          {/* Scanning overlay */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Dark overlay outside scanner frame */}
            <div className="absolute inset-0 bg-black/40" />
            
            {/* Clear center area */}
            <div className="absolute inset-12 bg-transparent" style={{
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)'
            }} />
            
            {/* Scanner frame with glow */}
            <div className="absolute inset-12 rounded-2xl border-2 border-primary/50 shadow-[0_0_20px_hsl(var(--primary)/0.3)]" />
            
            {/* Scanning line animation */}
            {!scanSuccess && !scanError && (
              <div className="absolute left-12 right-12 top-12 bottom-12 overflow-hidden rounded-2xl">
                <div className="scanner-line" />
              </div>
            )}
            
            {/* Corner indicators with glow */}
            <div className="absolute inset-12 pointer-events-none">
              {/* Top-left */}
              <div className="absolute -top-0.5 -left-0.5 w-8 h-8">
                <div className="absolute top-0 left-0 w-full h-1 bg-primary rounded-full shadow-[0_0_10px_hsl(var(--primary))]" />
                <div className="absolute top-0 left-0 w-1 h-full bg-primary rounded-full shadow-[0_0_10px_hsl(var(--primary))]" />
              </div>
              {/* Top-right */}
              <div className="absolute -top-0.5 -right-0.5 w-8 h-8">
                <div className="absolute top-0 right-0 w-full h-1 bg-primary rounded-full shadow-[0_0_10px_hsl(var(--primary))]" />
                <div className="absolute top-0 right-0 w-1 h-full bg-primary rounded-full shadow-[0_0_10px_hsl(var(--primary))]" />
              </div>
              {/* Bottom-left */}
              <div className="absolute -bottom-0.5 -left-0.5 w-8 h-8">
                <div className="absolute bottom-0 left-0 w-full h-1 bg-primary rounded-full shadow-[0_0_10px_hsl(var(--primary))]" />
                <div className="absolute bottom-0 left-0 w-1 h-full bg-primary rounded-full shadow-[0_0_10px_hsl(var(--primary))]" />
              </div>
              {/* Bottom-right */}
              <div className="absolute -bottom-0.5 -right-0.5 w-8 h-8">
                <div className="absolute bottom-0 right-0 w-full h-1 bg-primary rounded-full shadow-[0_0_10px_hsl(var(--primary))]" />
                <div className="absolute bottom-0 right-0 w-1 h-full bg-primary rounded-full shadow-[0_0_10px_hsl(var(--primary))]" />
              </div>
            </div>
          </div>
          
          {/* Flashlight toggle button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 border border-white/10"
            onClick={toggleFlashlight}
          >
            {flashlightOn ? (
              <Flashlight className="h-5 w-5 text-yellow-400" />
            ) : (
              <FlashlightOff className="h-5 w-5" />
            )}
          </Button>
          
          {/* Processing indicator */}
          {(scanning || galleryLoading) && !scanSuccess && !scanError && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
              <div className="bg-background/95 backdrop-blur-xl p-5 rounded-2xl flex items-center gap-4 shadow-2xl border border-border/50">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                  <div className="relative p-3 rounded-full bg-primary/10">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {galleryLoading ? t('qrScanner.readingFromImage') : t('qrScanner.processing')}
                  </p>
                  {detectedType && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {getQRTypeName(detectedType)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Success indicator */}
          {scanSuccess && (
            <div className="absolute inset-0 bg-gradient-to-b from-green-500/30 to-green-500/10 backdrop-blur-sm flex items-center justify-center animate-in fade-in zoom-in duration-300">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-30" />
                <div className="relative bg-gradient-to-br from-green-400 to-green-600 text-white p-6 rounded-full shadow-2xl shadow-green-500/30">
                  <CheckCircle2 className="h-12 w-12" />
                </div>
              </div>
            </div>
          )}
          
          {/* Error indicator */}
          {scanError && (
            <div className="absolute inset-0 bg-gradient-to-b from-red-500/30 to-red-500/10 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-300">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-30" />
                <div className="relative bg-gradient-to-br from-red-400 to-red-600 text-white p-6 rounded-full shadow-2xl shadow-red-500/30">
                  <XCircle className="h-12 w-12" />
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Bottom section */}
        <div className="p-4 space-y-3 bg-gradient-to-t from-background to-background/50">
          {/* Instruction */}
          <div className="flex items-center gap-2 justify-center text-muted-foreground">
            <Scan className="h-4 w-4" />
            <span className="text-sm">{t('qrScanner.instruction')}</span>
          </div>
          
          {/* Gallery Button */}
          <Button
            variant="outline"
            onClick={handleGalleryPick}
            disabled={scanning || galleryLoading}
            className="w-full flex items-center gap-3 py-6 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20 hover:border-primary/40 hover:bg-primary/10 transition-all duration-300"
          >
            <div className="p-2 rounded-lg bg-primary/10">
              {galleryLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              ) : (
                <ImageIcon className="h-5 w-5 text-primary" />
              )}
            </div>
            <span className="font-medium">{t('qrScanner.selectFromGallery')}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
